import type { TitleFilter } from '@/@types/graphql-types';
import { MonetizationType, SimilarTitlesSorting } from '@/@types/graphql-types';
import type { TitleDetail } from '@/interfaces/title-details-graphql';
import type { MaybeRefOrGetter } from '@vueuse/core';

import type {
	GetSingleQtvSimilarTitlesQuery,
	GetSingleQtvSimilarTitlesQueryVariables,
	SingleQtvSimilarTitleFragment,
} from '@/features/buybox/graphql/queries/GetSingleQTV.query';

import { GetSingleQtvSimilarTitlesDocument } from '@/features/buybox/graphql/queries/GetSingleQTV.query';

import type {
	GetSingleQtvTargetedTitleQuery,
	GetSingleQtvTargetedTitleQueryVariables,
} from '@/features/buybox/graphql/queries/GetSingleQtvTargetedTitle.query';
import { GetSingleQtvTargetedTitleDocument } from '@/features/buybox/graphql/queries/GetSingleQtvTargetedTitle.query';

import adminConfig from '@/features/promotions/config.json';
import {
	jwtvSimilarityThreshold,
	JWTV_UTOPIA_TITLES,
	JWTV_UTOPIA_SHORTNAME,
} from '@/helpers/ad/useJWTVUtopiaTargeting';
import { getTitleSqtvTitleId, useTitleSqtvTargeting } from '@/helpers/ad/useTitleSqtvTargeting';
import { captureMessageForSentry } from '@/helpers/sentry-helper';
import { useQuery } from '@/helpers/composables/useApollo';
import { useI18n } from '@/helpers/composables/useI18n';
import { storeToRefs } from 'pinia';
import { useLanguageStore } from '@/stores/pinia/useLanguageStore';

import { toValue } from '@vueuse/core';
import { computed } from 'vue';

const singleQTVBannerAndDisclaimer: Record<string, Record<string, { bannerText: string; disclaimerText: string }>> = {
	TW: {
		itu: {
			bannerText: '春節優惠: Apple TV 好戲 NT$15 起租，立即觀看',
			disclaimerText: '優惠期間 2 月 9 日至 22 日。僅限指定片單。新片 NT$190 起。新用戶可享免費試用一個月。',
		},
	},
	HK: {
		itu: {
			bannerText: '新春優惠: Apple TV 猛片 HK$8 起租，即刻睇戲',
			disclaimerText: '優惠期間 2 月 9 日至 22 日。僅限指定片單。新片 HK$58 起。新用戶可享免費試用一個月。',
		},
	},
};

export function useSingleQTV(
	title: MaybeRefOrGetter<TitleDetail>,
	providerShortName: MaybeRefOrGetter<string | null>,
	enabled: MaybeRefOrGetter<boolean>,
	experimentFilters?: MaybeRefOrGetter<TitleFilter | null>,
	includeClips?: MaybeRefOrGetter<boolean>
) {
	const { country, language } = storeToRefs(useLanguageStore());
	const { tc } = useI18n();
	const _title = toValue(title);

	const isEnabled = computed(() => toValue(enabled));
	const shortName = computed(() => toValue(providerShortName) || null);

	const imdbFilter = computed(() => (shortName.value === 'mxx' ? { imdbScore: { min: 7.5 } } : {}));

	// Only apply JWTV Utopia rules when querying the jwt provider specifically
	const isJWTVUtopiaActive = computed(
		() =>
			shortName.value === JWTV_UTOPIA_SHORTNAME &&
			country.value === 'IT' &&
			adminConfig.steering_overrides_global.jwtv_utopia_sqtv_italy
	);

	// Only apply title SQTV rules when querying a configured provider for that country
	const { isTitleTargetedSqtvActive } = useTitleSqtvTargeting(_title, isEnabled);

	// jwEntityIds - Limits the ids available for SQTV response. This is really not something we should do very often. Falls apart if the entityIds do not have corresponding package.shortName in the filter.
	const jwEntityIds = computed(() => {
		if (isJWTVUtopiaActive.value) return { jwEntityIDs: JWTV_UTOPIA_TITLES };
		if (isTitleTargetedSqtvActive.value)
			return { jwEntityIDs: [getTitleSqtvTitleId(shortName.value, country.value)!] };
		return {};
	});

	const variables = computed<GetSingleQtvSimilarTitlesQueryVariables>(() => ({
		country: country.value,
		language: language.value,
		first: 1,
		titleId: _title.__typename === 'Season' ? _title.show.id : _title.id,
		filters: {
			packages: shortName.value ? [shortName.value] : [],
			...(toValue(experimentFilters) ?? {}),
			...imdbFilter.value,
			...jwEntityIds.value,
		},
		offerFilters: {
			packages: shortName.value ? [shortName.value] : [],
			bestOnly: true,
		},
		sorting: SimilarTitlesSorting['ImdbScore'],
		withClips: toValue(includeClips) ?? false,
	}));

	const enableQuery = computed(
		() => isEnabled.value && toValue(providerShortName) !== null && !isTitleTargetedSqtvActive.value
	);

	const { result, loading, onError } = useQuery<
		GetSingleQtvSimilarTitlesQuery,
		GetSingleQtvSimilarTitlesQueryVariables
	>(GetSingleQtvSimilarTitlesDocument, variables, { enabled: enableQuery });

	onError(error => {
		captureMessageForSentry('[Graphql GetSingleQtvSimilarTitlesQuery]', {
			where: '[SingleQTV.vue]',
			error,
		});
	});

	const targetedVariables = computed<GetSingleQtvTargetedTitleQueryVariables>(() => ({
		country: country.value,
		language: language.value,
		targetTitleId: getTitleSqtvTitleId(shortName.value, country.value)!,
		offerFilters: {
			packages: shortName.value ? [shortName.value] : [],
			bestOnly: true,
		},
		withClips: toValue(includeClips) ?? false,
	}));

	const enableTargetedQuery = computed(
		() => isEnabled.value && toValue(providerShortName) !== null && isTitleTargetedSqtvActive.value
	);

	const {
		result: targetedResult,
		loading: targetedLoading,
		onError: onTargetedError,
	} = useQuery<GetSingleQtvTargetedTitleQuery, GetSingleQtvTargetedTitleQueryVariables>(
		GetSingleQtvTargetedTitleDocument,
		targetedVariables,
		{ enabled: enableTargetedQuery }
	);

	onTargetedError(error => {
		captureMessageForSentry('[Graphql GetSingleQtvTargetedTitleQuery]', {
			where: '[SingleQTV.vue]',
			error,
		});
	});

	const sqtvBannerAndDisclaimer = computed(() => {
		if (!shortName.value) return null;
		return singleQTVBannerAndDisclaimer[country.value]?.[shortName.value] ?? null;
	});

	const combinedLoading = computed(() => loading.value || targetedLoading.value);

	const similarTitle = computed(() => {
		if (isTitleTargetedSqtvActive.value) {
			const node = targetedResult.value?.node;
			if (node?.__typename !== 'Movie' && node?.__typename !== 'Show') return null;
			return { node, similarityScore: null };
		}
		if (result.value?.node?.__typename !== 'Movie' && result.value?.node?.__typename !== 'Show') return null;
		const edge = result.value.node.similarTitlesV2.edges?.[0] ?? null;

		// JWTV Utopia Italy - because of limited titles we need to ensure SQTV similarity is better than terrible
		if (isJWTVUtopiaActive.value && (edge?.similarityScore ?? 0) < jwtvSimilarityThreshold) return null;
		return edge;
	});

	const isSQTVEmpty = computed(() => !combinedLoading.value && similarTitle.value == null);

	/*
	 * SQTV tags ->
	 * Tags only apply if the info is common to the current title and the returned SQTV similar title.
	 *
	 * Prio order (max 3, min 1)
	 * 1. common title tags
	 * 2. common genre tags
	 * 3. Highly Rated (if both titles have imdb score > 7)
	 * 4. Free Trial (if title has offer with Free Trial & SQTV offer has Free Trial)
	 * 5. Free (if title has Free offer & SQTV offer is Free)
	 * 6. Similar (generic, but we need something in the case that all other tags fail.)
	 */
	function getSQTVTags({
		title,
		similarTitle,
		monetizationType,
		allowFreeTag,
	}: {
		title: TitleDetail;
		similarTitle: SingleQtvSimilarTitleFragment | TitleDetail | null | undefined;
		monetizationType: MonetizationType | null | undefined;
		allowFreeTag: boolean;
	}): string[] {
		// 1: common tags
		const titleTagNames = (title.content.tags ?? []).map(tag => tag.translatedName);
		const similarTagNames = similarTitle?.content?.tags?.map(tag => tag.translatedName) ?? [];
		const commonTags = titleTagNames.filter(tag => similarTagNames.includes(tag));

		// 2: common genres
		const titleGenreNames = title.content.genres.map(g => g.translation);
		const similarGenreNames = similarTitle?.content?.genres?.map(g => g.translation) ?? [];
		const commonGenres = titleGenreNames.filter(g => similarGenreNames.includes(g) && !commonTags.includes(g));

		const tags = [...commonTags, ...commonGenres].slice(0, 3);

		// 3: both titles Highly Rated
		if (tags.length < 3) {
			const titleImdb = title.content.scoring?.imdbScore ?? 0;
			const similarImdb = similarTitle?.content?.scoring?.imdbScore ?? 0;
			if (titleImdb > 7 && similarImdb > 7) tags.push(tc('WEBAPP_HIGHLY_RATED'));
		}

		// 4 & 5: Free / Free Trial tags
		if (tags.length < 3 && monetizationType && allowFreeTag) {
			if (monetizationType === MonetizationType.Flatrate) {
				// Free Trial: title must have a flatrate offer with a trial plan or child plans
				if (
					title.offers?.some(
						o =>
							o.monetizationType === MonetizationType.Flatrate &&
							(o.package.planOffers?.some(p => p.isTrial || (p.children?.length ?? 0) > 0) ?? false)
					)
				)
					tags.push(tc('WEBAPP_FREE_TRIAL_TAG'));
			} else if (monetizationType === MonetizationType.Free) {
				if (title.offers?.some(o => o.monetizationType === MonetizationType.Free)) tags.push(tc('WEBAPP_FREE'));
			}
		}

		// 6: Similar - fallback tag
		if (tags.length < 3) tags.push(tc('WEBAPP_SIMILAR'));

		return tags;
	}

	return {
		similarTitle,
		loading: combinedLoading,
		isSQTVEmpty,
		sqtvBannerAndDisclaimer,
		getSQTVTags,
	};
}
